import { BaseService } from './BaseService';
import type { PagedResult } from '../types/tenant';

/**
 * Workflow definition DTO.
 */
export interface WorkflowDefinitionDto {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  version: string;
  workflowType: string;
  patternType: string;
  supportsCheckpointing: boolean;
  supportsStreaming: boolean;
  tenantId: string;
  projectId?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Workflow detail DTO (includes full JSON).
 */
export interface WorkflowDetailDto extends WorkflowDefinitionDto {
  workflowJson: string;
  startAgentDefinitionId?: string;
  metadataJson?: string;
}

/**
 * Workflow execution DTO.
 */
export interface WorkflowExecutionDto {
  id: string;
  workflowDefinitionId: string;
  workflowName: string;
  status: string;
  currentNodeId?: string;
  correlationId?: string;
  startedAt?: string;
  completedAt?: string;
  pausedAt?: string;
  errorMessage?: string;
  tenantId: string;
  projectId?: string;
  createdAt: string;
}

/**
 * Workflow checkpoint DTO.
 */
export interface WorkflowCheckpointDto {
  id: string;
  executionId: string;
  nodeId: string;
  sequenceNumber: number;
  stateJson: string;
  createdAt: string;
}

/**
 * Create workflow request.
 */
export interface CreateWorkflowRequest {
  name: string;
  description?: string;
  workflowJson: string;
  workflowType: string;
  patternType: string;
  startAgentDefinitionId?: string;
  supportsCheckpointing: boolean;
  supportsStreaming: boolean;
  version: string;
  metadataJson?: string;
}

/**
 * Update workflow request.
 */
export interface UpdateWorkflowRequest {
  name: string;
  description?: string;
  workflowJson: string;
  isActive: boolean;
  version: string;
  metadataJson?: string;
}

/**
 * Execute workflow request.
 */
export interface ExecuteWorkflowRequest {
  parameters?: Record<string, unknown>;
  correlationId?: string;
}

/**
 * Execute workflow response.
 */
export interface ExecuteWorkflowResponse {
  executionId: string;
  workflowDefinitionId: string;
  status: string;
}

/**
 * Resume workflow request.
 */
export interface ResumeWorkflowRequest {
  triggerData?: unknown;
}

/**
 * Workflow filters for queries.
 */
export interface WorkflowFilters {
  pageNumber?: number;
  pageSize?: number;
  isActive?: boolean;
  searchTerm?: string;
}

/**
 * Workflow service for handling workflow operations.
 */
export class WorkflowService extends BaseService {
  /**
   * Gets a paginated list of workflow definitions.
   */
  async getWorkflows(filters?: WorkflowFilters): Promise<PagedResult<WorkflowDefinitionDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
    if (filters?.searchTerm) queryParams.append('searchTerm', filters.searchTerm);

    const url = `/workflows${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<WorkflowDefinitionDto>>(url);
  }

  /**
   * Gets a workflow definition by ID.
   */
  async getWorkflow(id: string): Promise<WorkflowDetailDto> {
    return this.get<WorkflowDetailDto>(`/workflows/${id}`);
  }

  /**
   * Creates a new workflow definition.
   */
  async createWorkflow(data: CreateWorkflowRequest): Promise<WorkflowDetailDto> {
    return this.post<WorkflowDetailDto>('/workflows', data);
  }

  /**
   * Updates an existing workflow definition.
   */
  async updateWorkflow(id: string, data: UpdateWorkflowRequest): Promise<WorkflowDetailDto> {
    return this.put<WorkflowDetailDto>(`/workflows/${id}`, data);
  }

  /**
   * Deletes a workflow definition.
   */
  async deleteWorkflow(id: string): Promise<void> {
    return this.delete<void>(`/workflows/${id}`);
  }

  /**
   * Executes a workflow.
   */
  async executeWorkflow(id: string, request?: ExecuteWorkflowRequest): Promise<ExecuteWorkflowResponse> {
    return this.post<ExecuteWorkflowResponse>(`/workflows/${id}/execute`, request);
  }

  /**
   * Resumes a paused workflow execution.
   */
  async resumeWorkflow(executionId: string, request?: ResumeWorkflowRequest): Promise<WorkflowExecutionDto> {
    return this.post<WorkflowExecutionDto>(`/workflows/executions/${executionId}/resume`, request);
  }

  /**
   * Gets checkpoints for a workflow execution.
   */
  async getCheckpoints(executionId: string): Promise<WorkflowCheckpointDto[]> {
    return this.get<WorkflowCheckpointDto[]>(`/workflows/executions/${executionId}/checkpoints`);
  }
}

export const workflowService = new WorkflowService();
